import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PRODUCTS } from '../utils/products';
import { getDiscountedPrice, hasDiscount, getDiscount, DISCOUNT_CONFIG } from '../utils/discounts';
import { isBestseller } from '../utils/bestsellers';
import ImageCarousel from '../components/ImageCarousel';
import PageLoader from '../components/PageLoader';
import StarRating from '../components/StarRating';
import heroImg1   from '../utils/hero_img/img_1.webp';
import heroImg2   from '../utils/hero_img/img_2.webp';
import heroImg3   from '../utils/hero_img/img_3.webp';
import heroImg4   from '../utils/hero_img/img_4.webp';
import heroAsset2 from '../utils/hero_img/asset_2.webp';
import heroAsset3 from '../utils/hero_img/asset_3.webp';
import heroAsset4 from '../utils/hero_img/asset_4.webp';
import homeImg1001 from '../utils/home_img_1001.webp';
import whyImg from '../utils/img1001.webp';
import trustFarmAltitude from '../utils/base_altitude.webp';
import trustWomenFarmer from '../utils/women_farmer.webp';
import trustPlantProtein from '../utils/platnt_protein.webp';
import trustGiTag from '../utils/gi_tag.webp';
import './Home.css';

const vidHomePg = 'https://d2lruhji8pyyv2.cloudfront.net/home_pg_1.mp4';

// ===== NEW REDESIGN — WAVE THEME — START =====
/* Palette (client-supplied):
   Primary   #826845 #618E69 #5D9C9D #03605C
   Secondary #655F59 #B6787F #95373A #D76427 #F8F3EB #DBD3A8 #B5882D #A56650 */
const H = {
  // Primary
  BRAND_BROWN:  '#826845',
  BRAND_GREEN:  '#618E69',
  BRAND_TEAL:   '#5D9C9D',
  DEEP_FOREST:  '#03605C',

  // Secondary
  INK:              '#655F59',
  ROSE:             '#B6787F',
  BURGUNDY:         '#95373A',
  SEAL_TERRACOTTA:  '#D76427',
  CREAM:            '#F8F3EB',
  PALE_SAGE:        '#DBD3A8',
  MEADOW_GOLD:      '#B5882D',
  OCHRE:            '#A56650',

  // Derived tones (kept within family of the supplied palette)
  CREAM_SOFT:      '#F1E7CE',
  PAPER:           '#F8F3EB',
  DEEP_FOREST_DK:  '#024442',
  FOREST_SHADOW:   '#013532',
  SKY:             '#5D9C9D',
  SKY_PALE:        '#B7CFCF',
  PEAK_BLUE:       '#03605C',
  PEAK_BLUE_DK:    '#024442',
  MUSTARD:         '#B5882D',
  GOLD_LINE:       '#B5882D',
  SOIL_OLIVE:      '#618E69',
  SAGE:            '#618E69',
};

/* Instagram posts shown at the bottom of the home page. Paste any Instagram
   post OR reel URL here — the parser below extracts /p/ID or /reel/ID. */
const INSTAGRAM_POSTS = [
  'https://www.instagram.com/p/DVmE2vzDO9G/',
  'https://www.instagram.com/p/DcBTaszii-x/',
  'https://www.instagram.com/p/DJHA05myI5P/',
  'https://www.instagram.com/p/DGV4VkVy2f1/',
  'https://www.instagram.com/p/DIOOm8uSKUz/'
];

function toInstagramEmbed(url) {
  const m = url.match(/instagram\.com\/(p|reel)\/([^/?#]+)/);
  return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed` : null;
}

/* Dummy customer reviews shown in the marquee at the bottom of Home.
   Replace this array with real reviews when they're ready — the component
   maps over it and duplicates it internally for the seamless loop. */
const CUSTOMER_REVIEWS = [
  { name: 'Rahul Sharma',  location: 'Mumbai, India',    source: 'Amazon',  rating: 5,
    review: 'Absolutely loved the product. The quality was excellent and the experience was even better than expected.' },
  { name: 'Priya Mehta',   location: 'Delhi, India',     source: 'Website', rating: 5,
    review: 'Amazing quality and excellent customer service. Would definitely recommend this to others.' },
  { name: 'Arjun Kumar',   location: 'Bangalore, India', source: 'Amazon',  rating: 5,
    review: 'The product arrived quickly and looks exactly like the pictures. Very happy with my purchase.' },
  { name: 'Sneha Patel',   location: 'Ahmedabad, India', source: 'Website', rating: 5,
    review: 'Beautiful product and great overall experience. I will definitely be ordering again.' },
  { name: 'Vikram Iyer',   location: 'Chennai, India',   source: 'Google',  rating: 5,
    review: 'Truly premium taste and quality. You can tell it is made with care and traditional methods.' },
  { name: 'Anjali Nair',   location: 'Kochi, India',     source: 'Amazon',  rating: 4,
    review: 'Great product, packaging could be a bit better but the ghee itself is fantastic. Reordering soon.' },
];

/* ------- Brand icon medallions — line-art on tinted disc, mirroring the OUO icon system ------- */

function IconDisc({ tone, children, size = 68 }) {
  return (
    <svg viewBox="0 0 68 68" width={size} height={size} aria-hidden="true" focusable="false">
      <circle cx="34" cy="34" r="33" fill={tone}/>
      <g stroke={H.CREAM} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </g>
    </svg>
  );
}

const IcoFarmAltitude = ({ size }) => (
  <IconDisc tone={H.BRAND_TEAL} size={size}>
    <path d="M12,42 L22,26 L28,34 L34,24 L42,38 L48,30 L56,42"/>
    <path d="M14,50 C22,44 30,52 40,46 C48,42 52,50 56,48" opacity="0.85"/>
  </IconDisc>
);

const IcoWomenFarmers = ({ size }) => (
  <IconDisc tone={H.BRAND_BROWN} size={size}>
    <circle cx="26" cy="24" r="4"/>
    <path d="M20,44 C22,34 30,34 32,44"/>
    <circle cx="42" cy="24" r="4"/>
    <path d="M36,44 C38,34 46,34 48,44"/>
    <path d="M12,52 C22,46 46,46 56,52"/>
  </IconDisc>
);

const IcoPlantProtein = ({ size }) => (
  <IconDisc tone={H.BRAND_GREEN} size={size}>
    <ellipse cx="34" cy="26" rx="7" ry="9"/>
    <path d="M26,36 C22,44 28,50 34,48 C40,50 46,44 42,36"/>
    <path d="M34,20 L34,50"/>
  </IconDisc>
);

const IcoHowToCook = ({ size }) => (
  <IconDisc tone={H.MEADOW_GOLD} size={size}>
    <path d="M14,30 L54,30 L50,50 L18,50 Z"/>
    <path d="M14,30 L12,26 M54,30 L56,26"/>
    <path d="M22,22 C22,18 26,18 26,22 M32,22 C32,18 36,18 36,22 M42,22 C42,18 46,18 46,22"/>
  </IconDisc>
);

/* ------- Hero landscape — now a photograph-style artwork (mountain_1.png) ------- */
// function HeroLandscape() {
//   return (
//     /* <picture> lets the browser pick the right art-directed image for the viewport:
//          - >= 900px: the wide letterbox (2.9:1) — short enough to fit above-the-fold on desktop
//          - default : the tall aspect (1.52:1) — fills phone/tablet screens comfortably
//        Only one image is downloaded per client. Alt lives on the fallback <img>. */
//     <picture>
//       {/* Desktop breakpoint — AVIF first, WebP fallback for older Safari */}
//       <source media="(min-width: 900px)" srcSet={mountainDesktopAvif} type="image/avif" />
//       <source media="(min-width: 900px)" srcSet={mountainDesktopWebp} type="image/webp" />
//       {/* Mobile / default — AVIF first, WebP fallback */}
//       <source srcSet={mountainMobileAvif} type="image/avif" />
//       <source srcSet={mountainMobileWebp} type="image/webp" />
//       <img
//         className="wtn-hero__landscape"
//         src={mountainMobileWebp}
//         alt=""
//         aria-hidden="true"
//         width="2880"
//         height="1889"
//         decoding="async"
//         loading="eager"
//       />
//     </picture>
//   );
// }

/* Generic asymmetric wave divider */
function WaveDivider({ height = 90, palette, flip = false }) {
  const layers = palette || [H.CREAM, H.CREAM_SOFT];
  const style = { transform: flip ? 'scaleY(-1)' : 'none', display: 'block', width: '100%', height };
  return (
    <svg viewBox="0 0 1440 100" preserveAspectRatio="none" aria-hidden="true" focusable="false" style={style}>
      <path d="M0,32 C220,4 460,72 720,36 C980,0 1220,60 1440,28 L1440,100 L0,100 Z"
            fill={layers[0]} opacity="0.95"/>
      {layers[1] && (
        <path d="M0,52 C260,28 480,80 780,52 C1060,26 1260,72 1440,48 L1440,100 L0,100 Z"
              fill={layers[1]} opacity="0.65"/>
      )}
      {layers[2] && (
        <path d="M0,72 C300,54 520,90 800,72 C1080,54 1260,88 1440,72 L1440,100 L0,100 Z"
              fill={layers[2]} opacity="0.4"/>
      )}
      <path d="M0,34 C220,6 460,74 720,38 C980,2 1220,62 1440,30"
            stroke={H.GOLD_LINE} strokeWidth="1.1" fill="none" opacity="0.45"/>
    </svg>
  );
}

/* Wave-cut card image bottom */
function CardWaveCut({ fill = H.CREAM_SOFT }) {
  return (
    <svg className="wtn-pcard__wavecut" viewBox="0 0 400 40" preserveAspectRatio="none"
         aria-hidden="true" focusable="false">
      <path d="M0,20 C80,4 160,32 220,18 C280,4 340,28 400,14 L400,40 L0,40 Z" fill={fill}/>
      <path d="M0,22 C80,6 160,34 220,20 C280,6 340,30 400,16"
            stroke={H.GOLD_LINE} strokeWidth="1" fill="none" opacity="0.55"/>
    </svg>
  );
}

/* Trust medallion — brand PNG logo + label pair */
function Medallion({ title, sub, img, alt, imgStyle }) {
  return (
    <div className="wtn-medal">
      <img src={img} alt={alt || `${title} ${sub}`} className="wtn-medal__img" style={imgStyle} />
      <div className="wtn-medal__text">
        <div className="wtn-medal__title">{title}</div>
        <div className="wtn-medal__sub">{sub}</div>
      </div>
    </div>
  );
}

function Home() {
  const heroRef       = useRef(null);
  const heroScrollRef = useRef(null);
  const img1Ref       = useRef(null);
  const img2Ref       = useRef(null);
  const img3Ref       = useRef(null);
  const img4Ref       = useRef(null);
  const text1Ref      = useRef(null);
  const text2Ref      = useRef(null);
  const text3Ref      = useRef(null);
  const text4Ref      = useRef(null);
  const asset2Ref     = useRef(null);
  const asset3Ref     = useRef(null);
  const asset4Ref     = useRef(null);
  const skipBtnRef    = useRef(null);
  const trustRef      = useRef(null);

  // Keep hero height = viewport - real navbar height, recalculated on resize.
  // useLayoutEffect runs before the browser paints so there is no visible jump.
  useLayoutEffect(() => {
    const sync = () => {
      const nav = document.querySelector('.wtn-header');
      if (!nav || !heroRef.current) return;
      const navH = nav.offsetHeight;
      heroRef.current.style.height = (window.innerHeight - navH) + 'px';
      heroRef.current.style.top    = navH + 'px';
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  // Scroll-reveal: single IntersectionObserver marks tagged elements when they
  // cross into view. One-shot per element (unobserve after firing). Skipped
  // entirely under prefers-reduced-motion — elements paint instantly.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = document.querySelectorAll('.wtn-reveal, .wtn-reveal-group');
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Hero scroll animation — rAF loop reading BoundingClientRect (Lenis-compatible).
  // State is derived from scroll progress each frame so backward scroll reverses automatically.
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const clamp   = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const easeOut = t => 1 - Math.pow(1 - t, 2);
    const P       = 1 / 11;
    const phaseP  = (prog, i) => clamp((prog - i * P) / P, 0, 1);

    const handleSkipClick = () => {
      const target = trustRef.current;
      if (!target) return;
      if (window.lenis) {
        window.lenis.scrollTo(target, {
          duration: 1.5,
          easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });
      } else {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const btn = skipBtnRef.current;
    if (btn) btn.addEventListener('click', handleSkipClick);

    let rafId;
    const frame = () => {
      const wrapper = heroScrollRef.current;
      if (!wrapper) { rafId = requestAnimationFrame(frame); return; }

      const rect     = wrapper.getBoundingClientRect();
      const total    = wrapper.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const prog     = clamp(scrolled / total, 0, 1);

      // Background images — each fades in over its dedicated transition phase,
      // completing before the next scene's text slides in.
      img1Ref.current.style.opacity = String(1 - phaseP(prog, 2));

      const img2In  = phaseP(prog, 2);
      const img2Out = phaseP(prog, 5);
      img2Ref.current.style.opacity = String(clamp(img2In * (1 - img2Out), 0, 1));

      const img3In  = phaseP(prog, 5);
      const img3Out = phaseP(prog, 8);
      img3Ref.current.style.opacity = String(clamp(img3In * (1 - img3Out), 0, 1));

      const img4In  = phaseP(prog, 8);
      img4Ref.current.style.opacity = String(img4In);

      // Text banners: slide in after bg is ready, hold one phase, fade out during bg transition
      const applyText = (el, slidePhase, fadePhase) => {
        const st = easeOut(phaseP(prog, slidePhase));
        const ft = phaseP(prog, fadePhase);
        el.style.opacity = String(clamp(st - ft, 0, 1));
        el.style.setProperty('--tx', `${-100 * (1 - st)}vw`);
      };
      // OLD — text1 slides in from left on scroll (restore these 2 lines to revert):
      // applyText(text1Ref.current, 0, 2);

      // NEW — text1 visible on load, fades out at phase 2 when user scrolls
      const ft1 = phaseP(prog, 2);
      text1Ref.current.style.opacity = String(clamp(1 - ft1, 0, 1));
      text1Ref.current.style.setProperty('--tx', '0vw');

      applyText(text2Ref.current, 3, 5);
      applyText(text3Ref.current, 6, 8);

      const t4 = easeOut(phaseP(prog, 9));
      text4Ref.current.style.opacity = String(t4);
      text4Ref.current.style.setProperty('--tx', `${-100 * (1 - t4)}vw`);

      // Asset images: slide in alongside their text banner, fade out together
      const applyAsset = (el, slidePhase, fadePhase) => {
        const st = easeOut(phaseP(prog, slidePhase));
        const ft = phaseP(prog, fadePhase);
        el.style.opacity = String(clamp(st - ft, 0, 1));
        el.style.setProperty('--atx', `${100 * (1 - st)}vw`);
      };
      applyAsset(asset2Ref.current, 3, 5);
      applyAsset(asset3Ref.current, 6, 8);

      const a4 = easeOut(phaseP(prog, 9));
      asset4Ref.current.style.opacity = String(a4);
      asset4Ref.current.style.setProperty('--atx', `${100 * (1 - a4)}vw`);

      // Skip button: visible while in hero section (prog < 1), fades out after
      if (skipBtnRef.current) {
        const inHero = scrolled >= 0 && prog < 1;
        skipBtnRef.current.style.opacity       = inHero ? '1' : '0';
        skipBtnRef.current.style.pointerEvents = inHero ? 'auto' : 'none';
      }

      rafId = requestAnimationFrame(frame);
    };
    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
      if (btn) btn.removeEventListener('click', handleSkipClick);
    };
  }, []);

  const scopedStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
    .wtn-home, .wtn-home * { box-sizing: border-box; }
    .wtn-home {
      background: ${H.CREAM};
      color: ${H.INK};
      font-family: -apple-system, "Segoe UI", system-ui, sans-serif;
      overflow-x: clip;
    }

    /* ---------- HERO SCROLL ANIMATION ---------- */
    .wtn-hs-wrapper {
      height: 1650vh;
      position: relative;
    }
    .wtn-hero {
      position: sticky;
      width: 100%;
      /* JS sets height and top via heroRef useLayoutEffect */
      height: calc(100svh - 110px);
      top: 110px;
      overflow: hidden;
      background: ${H.CREAM};
      display: block;
    }

    /* Background images — stacked, bottom-anchored, slightly downscaled */
    .wtn-hs-img {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      top: auto;
      width: 100%;
      height: 100%;
      object-fit: initial;
      object-position: initial;
      will-change: opacity;
      user-select: none;
      pointer-events: none;
    }
    .wtn-hs-img:nth-child(2),
    .wtn-hs-img:nth-child(3),
    .wtn-hs-img:nth-child(4) { opacity: 0; }

    /* Text banners — slide in from left, rest on left side */
    .wtn-hs-text {
      position: absolute;
      left: 0;
      top: 55%;
      transform: translateX(var(--tx, -100vw)) translateY(-50%);
      will-change: transform, opacity;
      opacity: 0;
      z-index: 10;
      background: rgb(246,244,234);
      color: ${H.DEEP_FOREST};
      width: 50vw;
      padding: 1.25rem 1.5rem 1.25rem 2rem;
      border-top-right-radius: 25px;
      border-bottom-right-radius: 25px;
      box-shadow: 4px 8px 32px rgba(0,0,0,0.22);
    }
    .wtn-hs-text__head {
      font-family: Georgia, "Iowan Old Style", serif;
      font-size: clamp(1.2rem, 2.4vw, 4.5rem);
      font-weight: 700;
      line-height: 1.25;
      margin: 0 0 0.75rem;
      color: ${H.DEEP_FOREST};
    }
    .wtn-hs-text__body {
      font-size: clamp(0.82rem, 1.3vw, 2.2rem);
      line-height: 1.7;
      margin: 0;
      color: grey;
    }

    /* Asset images — slide in from right */
    .wtn-hs-asset {
      position: absolute;
      right: 2rem;
      bottom: 0;
      max-height: 35vh;
      max-width: calc(46vw - 2rem);
      width: auto;
      transform: translateX(var(--atx, 100vw));
      will-change: transform, opacity;
      opacity: 0;
      z-index: 10;
      pointer-events: none;
      user-select: none;
    }

    /* Asset 2 — right-anchored, shifted above centre in the hero */
    .wtn-hs-asset--mid {
      left: auto;
      right: -6vw;
      top: clamp(15%, 30vh, 38%);
      bottom: auto;
      max-height: 60vh;
      max-width: 50vw;
      transform: translateX(var(--atx, 100vw)) translateY(-50%);
    }

    /* Skip intro button — above WhatsApp icon */
    .wtn-hs-skip {
      position: fixed;
      bottom: 100px;
      left: 24px;
      z-index: 1001;
      background: rgba(248,243,235,0.88);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      color: ${H.DEEP_FOREST};
      border: 1.5px solid ${H.DEEP_FOREST};
      border-radius: 999px;
      padding: 8px 18px;
      font-size: 0.75rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      font-weight: 700;
      cursor: pointer;
      opacity: 0;
      pointer-events: none;
      transition: opacity 300ms ease, background 180ms ease, color 180ms ease, transform 180ms ease;
    }
    .wtn-hs-skip:hover {
      background: ${H.DEEP_FOREST};
      color: ${H.CREAM};
      transform: translateY(-2px);
    }

    /* Mobile adjustments */
    /* Mobile-only tagline — hidden on desktop */
    .wtn-hs-tagline-mobile { display: none; }

    @media (max-width: 768px) {
      /*
       * All images are 1921×1013 px → aspect ratio 52.73%.
       * At width:100vw the natural height is exactly 52.73vw.
       * Everything below is anchored to that single number so layout
       * stays consistent at every mobile screen size.
       */

      /*
       * IMG_OFFSET: how far down from the hero top the mountain image starts.
       * Change this one value to reposition the image (and everything tied to it).
       */

      /* Background images: offset from top, full width, natural height — never crops */
      .wtn-hs-img {
        top: 25%;
        bottom: auto;
        height: auto;
        width: 100%;
        object-fit: initial;
        object-position: initial;
      }

      /* Text banners: flush left, anchored from top so they never overflow upward */
      .wtn-hs-text {
        left: 0;
        width: 82vw;
        top: 3%;
        bottom: auto;
        transform: translateX(var(--tx, -100vw));
        border-radius: 0 14px 14px 0;
        text-align: left;
        padding: 0.75rem 0.85rem 0.75rem 1rem;
      }
      .wtn-hs-text__head { font-size: clamp(0.8rem, 3.2vw, 0.95rem); }
      .wtn-hs-text__body { font-size: clamp(0.62rem, 2.4vw, 0.75rem); }

      /* Asset images: bottom of mountain image (top = IMG_OFFSET + image natural height) */
      .wtn-hs-asset {
        top: calc(25% + 52.73vw);
        bottom: auto;
        left: auto;
        right: 0;
        width: 50vw;
        height: auto;
        max-height: 52.73vw;
        transform: translateX(var(--atx, 100vw)) translateY(-100%);
      }

      .wtn-hs-asset--mid {
        left: auto;
        right: -5vw;
        top: calc(25% + 52.73vw * 0.3);
        width: 48vw;
        max-height: 48vw;
        transform: translateX(var(--atx, 100vw)) translateY(-50%);
      }

      /* Tagline shown below the mountain image on mobile */
      .wtn-hs-tagline-mobile {
        display: flex;
        flex-direction: column;
        position: absolute;
        top: calc(10% + 52.73vw + 1rem);
        bottom: 0;
        left: 0;
        right: 0;
        align-items: center;
        justify-content: center;
        padding: 1rem 1.5rem;
        z-index: 5;
        pointer-events: none;
        font-family: Georgia, "Iowan Old Style", serif;
        color: ${H.DEEP_FOREST};
        text-align: center;
        gap: 0.35rem;
      }
      .wtn-hs-tagline-mobile .tm-label {
        font-size: clamp(0.75rem, 3.4vw, 1.1rem);
        font-weight: 400;
        line-height: 1.4;
      }
      .wtn-hs-tagline-mobile .tm-heading {
        font-size: clamp(1.1rem, 5.2vw, 1.75rem);
        font-weight: 800;
        line-height: 1.25;
        letter-spacing: 0.01em;
      }

      .wtn-hs-skip { bottom: 90px; left: 20px; }
    }

    /* Reduced motion — show first scene, no overlays */
    @media (prefers-reduced-motion: reduce) {
      .wtn-hs-img:not(:nth-child(1)) { opacity: 0 !important; }
      .wtn-hs-text, .wtn-hs-asset, .wtn-hs-skip { display: none; }
    }

    .wtn-btn {
      display: inline-flex; align-items: center; gap: 8px;
      text-decoration: none;
      padding: 14px 26px; font-size: 12.5px; letter-spacing: 0.24em;
      text-transform: uppercase; font-weight: 700;
      border: 1.5px solid transparent;
      transition: transform 180ms ease, background 180ms ease, color 180ms ease, border-color 180ms ease;
    }
    .wtn-btn--primary { background: ${H.DEEP_FOREST}; color: ${H.CREAM}; border-color: ${H.DEEP_FOREST}; }
    .wtn-btn--primary:hover { background: ${H.SEAL_TERRACOTTA}; border-color: ${H.SEAL_TERRACOTTA}; transform: translateY(-1px); }
    .wtn-btn--ghost { background: ${H.CREAM}; color: ${H.DEEP_FOREST}; border-color: ${H.DEEP_FOREST}; }
    .wtn-btn--ghost:hover { background: ${H.DEEP_FOREST}; color: ${H.CREAM}; border-color: ${H.DEEP_FOREST}; transform: translateY(-1px); }
    .wtn-btn--large { padding: 16px 32px; font-size: 13px; }

    /* ---------- TRUST STRIP ---------- */
    .wtn-trust { background: ${H.CREAM}; padding: 36px 28px 24px; }
    .wtn-trust__inner {
      max-width: 1200px; margin: 0 auto;
      display: flex; align-items: center; justify-content: space-evenly;
      flex-wrap: wrap; gap: 28px;
    }
    .wtn-medal {
      display: flex; flex-direction: column;
      align-items: center; gap: 8px;
      text-align: center;
    }

    .wtn-medal__img {
      width: 80px; height: 80px;
      object-fit: contain; display: block;
    }
    .wtn-trust__badge {
      object-fit: contain; display: block;
    }
    .wtn-medal__text { display: flex; flex-direction: column; align-items: center; gap: 2px; }
    .wtn-medal__title {
      font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase;
      font-weight: 700; color: ${H.DEEP_FOREST_DK};
    }
    .wtn-medal__sub {
      font-size: 13.5px; font-weight: 800; color: ${H.DEEP_FOREST};
    }

    /* ---------- SECTION SHELL ---------- */
    .wtn-section { position: relative; padding: 64px 28px 80px; }
    .wtn-section__inner { max-width: 1320px; margin: 0 auto; }
    .wtn-eyebrow {
      display: inline-block;
      background: ${H.DEEP_FOREST}; color: ${H.CREAM};
      font-size: 10.5px; letter-spacing: 0.28em; text-transform: uppercase;
      font-weight: 700; padding: 6px 12px;
    }
    .wtn-h2 {
      font-family: Georgia, "Iowan Old Style", serif;
      font-size: clamp(28px, 3.4vw, 42px);
      color: ${H.DEEP_FOREST_DK};
      margin: 14px 0 10px; line-height: 1.15;
    }
    .wtn-sub {
      max-width: 640px; color: ${H.INK}; opacity: 0.78;
      font-size: 15.5px; line-height: 1.6; margin: 0 0 32px;
    }
    .wtn-h2-rule { display: block; width: 84px; height: 6px; margin: 0 0 16px; }

    /* ---------- FEATURES ---------- */
    .wtn-features { background: ${H.CREAM_SOFT}; }
    .wtn-features__grid {
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px;
    }
    .wtn-feature { text-align: center; padding: 24px 16px; }
    .wtn-feature__icon {
      width: 78px; height: 78px; margin: 0 auto 14px;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .wtn-feature__title {
      font-size: 12px; letter-spacing: 0.24em; text-transform: uppercase;
      color: ${H.DEEP_FOREST}; font-weight: 700; margin: 0 0 6px;
    }
    .wtn-feature__text {
      font-size: 14px; color: ${H.INK}; opacity: 0.78; margin: 0;
    }

    /* ---------- PRODUCTS ---------- */
    .wtn-products { background: ${H.PAPER}; }
    .wtn-products__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 28px;
    }
    .wtn-pcard {
      position: relative;
      background: ${H.CREAM_SOFT};
      text-decoration: none; color: ${H.INK};
      display: flex; flex-direction: column;
      border: 1px solid rgba(30,90,85,0.10);
      transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease;
      overflow: hidden;
    }
    .wtn-pcard:hover {
      transform: translateY(-4px);
      border-color: ${H.MEADOW_GOLD};
      box-shadow: 0 10px 24px rgba(15,58,54,0.10);
    }
    .wtn-pcard__imgwrap {
      position: relative; overflow: hidden;
      background: ${H.CREAM};
      height: 240px;
    }
    /* Force the internal carousel + image to fill the wrap.
       .carousel is 100% wide but has no height by default. */
    .wtn-pcard__imgwrap .carousel,
    .wtn-pcard__imgwrap .carousel__container,
    .wtn-pcard__imgwrap .carousel__image-container {
      height: 100%; width: 100%;
    }
    .wtn-pcard__imgwrap .carousel__image {
      width: 100%; height: 100%; object-fit: contain;
      border-radius: 0;
    }
    .wtn-pcard__wavecut {
      position: absolute; left: 0; right: 0; bottom: -1px;
      width: 100%; height: 32px; pointer-events: none;
      z-index: 3;
    }
    .wtn-pcard__badge {
      position: absolute; top: 12px; left: 12px; z-index: 4;
      background: ${H.DEEP_FOREST}; color: ${H.CREAM};
      font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
      font-weight: 700; padding: 5px 10px;
    }
    .wtn-pcard__best {
      position: absolute; top: 12px; right: 12px; z-index: 4;
      background: ${H.MEADOW_GOLD}; color: ${H.FOREST_SHADOW};
      font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
      font-weight: 800; padding: 5px 10px;
    }
    .wtn-pcard__disc {
      position: absolute; bottom: 44px; right: 12px; z-index: 4;
      background: ${H.SEAL_TERRACOTTA}; color: ${H.CREAM};
      font-size: 11px; font-weight: 800; padding: 6px 10px;
      letter-spacing: 0.05em;
    }
    .wtn-pcard__body { padding: 18px 20px 20px; display: flex; flex-direction: column; gap: 10px; }
    .wtn-pcard__name {
      font-family: Georgia, "Iowan Old Style", serif;
      font-size: 20px; color: ${H.DEEP_FOREST_DK}; margin: 0;
      letter-spacing: -0.005em; line-height: 1.2;
    }
    .wtn-pcard__desc {
      font-size: 13.5px; color: ${H.INK}; opacity: 0.75;
      margin: 0; line-height: 1.55;
    }
    .wtn-pcard__benefits { display: flex; flex-wrap: wrap; gap: 6px; }
    .wtn-btag {
      font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
      font-weight: 600; color: ${H.SOIL_OLIVE};
      background: rgba(168,185,117,0.18);
      padding: 4px 8px; border: 1px solid rgba(111,131,64,0.28);
    }
    .wtn-pcard__foot {
      display: flex; align-items: center; justify-content: space-between;
      margin-top: 6px; padding-top: 12px;
      border-top: 1px dashed rgba(30,90,85,0.18);
    }
    .wtn-pcard__price { font-weight: 700; color: ${H.DEEP_FOREST_DK}; font-size: 15px; }
    .wtn-pcard__price--strike {
      text-decoration: line-through; color: ${H.INK}; opacity: 0.5;
      font-size: 13px; font-weight: 500; margin-right: 6px;
    }
    .wtn-pcard__price--now { color: ${H.SEAL_TERRACOTTA}; font-weight: 800; font-size: 15px; }
    .wtn-pcard__go {
      font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
      font-weight: 700; color: ${H.DEEP_FOREST};
    }
    .wtn-products__cta { text-align: center; margin-top: 40px; }

    /* ---------- WHY ---------- */
    .wtn-why { background: ${H.MEADOW_GOLD}; }
    .wtn-why .wtn-h2 { color: ${H.FOREST_SHADOW}; }
    .wtn-why .wtn-sub { color: ${H.FOREST_SHADOW}; opacity: 0.85; }
    .wtn-why .wtn-eyebrow { background: ${H.FOREST_SHADOW}; color: ${H.CREAM}; }
    .wtn-why__images {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 18px; margin-bottom: 40px;
    }
    .wtn-why__img {
      width: 100%; aspect-ratio: 4 / 3; object-fit: cover;
      border: 1.5px solid ${H.FOREST_SHADOW};
    }
    .wtn-why__grid {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
    }
    .wtn-wcard {
      background: ${H.CREAM};
      padding: 24px 22px 26px;
      border: 1px solid rgba(15,58,54,0.15);
    }
    .wtn-wcard h3 {
      margin: 0 0 10px;
      font-size: 13px; letter-spacing: 0.2em; text-transform: uppercase;
      color: ${H.DEEP_FOREST}; font-weight: 800;
    }
    .wtn-wcard p {
      margin: 0; font-size: 14px; line-height: 1.6;
      color: ${H.INK}; opacity: 0.8;
    }

    /* ---------- CTA ---------- */
    .wtn-cta {
      background: ${H.DEEP_FOREST_DK};
      color: ${H.CREAM};
      text-align: center;
    }
    .wtn-cta__inner { max-width: 800px; margin: 0 auto; padding: 20px 28px 40px; }
    .wtn-cta h2 {
      font-family: Georgia, "Iowan Old Style", serif;
      font-size: clamp(26px, 3.2vw, 40px);
      margin: 8px 0 12px; color: ${H.CREAM};
    }
    .wtn-cta p { font-size: 16px; opacity: 0.85; margin: 0 0 24px; color: ${H.CREAM}; }
    .wtn-cta .wtn-btn--primary {
      background: ${H.MEADOW_GOLD}; color: ${H.FOREST_SHADOW}; border-color: ${H.MEADOW_GOLD};
    }
    .wtn-cta .wtn-btn--primary:hover {
      background: ${H.SEAL_TERRACOTTA}; color: ${H.CREAM}; border-color: ${H.SEAL_TERRACOTTA};
    }
    .wtn-cta__eyebrow {
      display: inline-block;
      background: ${H.MEADOW_GOLD}; color: ${H.FOREST_SHADOW};
      font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase;
      font-weight: 800; padding: 6px 12px;
    }

    /* Instagram horizontal-scroll styles live in HSCROLL_INSTA_STYLES,
       injected by <HorizontalInstagram /> (mirrors the Products pattern). */
    .wtn-ihs-below {
      background: ${H.CREAM};
      text-align: center;
      padding: 8px 20px 40px;
    }
    .wtn-ihs-below .wtn-btn {
      display: inline-block;
    }

    /* ---------- SCROLL-REVEAL SYSTEM ----------
       Slow, curvy, organic. Signature move: eyebrow chips slide in from the
       left (like a stamp being placed), while headings and cards "settle" from
       scale(0.96) + translateY — the "grains falling into place" feel. */
    .wtn-reveal {
      opacity: 0;
      transform: translate3d(0, 28px, 0);
      transition:
        opacity 900ms cubic-bezier(0.22, 0.7, 0.2, 1),
        transform 900ms cubic-bezier(0.22, 0.7, 0.2, 1);
      will-change: opacity, transform;
    }
    .wtn-reveal.is-inview {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
    .wtn-reveal--from-left {
      transform: translate3d(-40px, 0, 0);
    }
    .wtn-reveal--from-left.is-inview {
      transform: translate3d(0, 0, 0);
    }
    .wtn-reveal--from-right {
      transform: translate3d(40px, 0, 0);
    }
    .wtn-reveal--from-right.is-inview {
      transform: translate3d(0, 0, 0);
    }
    .wtn-reveal--scale {
      transform: scale(0.94) translate3d(0, 18px, 0);
    }
    .wtn-reveal--scale.is-inview {
      transform: scale(1) translate3d(0, 0, 0);
    }

    /* Staggered container — children reveal one after another */
    .wtn-reveal-group > * {
      opacity: 0;
      transform: translate3d(0, 32px, 0) scale(0.96);
      transition:
        opacity 850ms cubic-bezier(0.22, 0.7, 0.2, 1),
        transform 850ms cubic-bezier(0.22, 0.7, 0.2, 1);
      will-change: opacity, transform;
    }
    .wtn-reveal-group.is-inview > * {
      opacity: 1;
      transform: translate3d(0, 0, 0) scale(1);
    }
    .wtn-reveal-group.is-inview > *:nth-child(1)  { transition-delay:  60ms; }
    .wtn-reveal-group.is-inview > *:nth-child(2)  { transition-delay: 140ms; }
    .wtn-reveal-group.is-inview > *:nth-child(3)  { transition-delay: 220ms; }
    .wtn-reveal-group.is-inview > *:nth-child(4)  { transition-delay: 300ms; }
    .wtn-reveal-group.is-inview > *:nth-child(5)  { transition-delay: 380ms; }
    .wtn-reveal-group.is-inview > *:nth-child(6)  { transition-delay: 460ms; }
    .wtn-reveal-group.is-inview > *:nth-child(7)  { transition-delay: 540ms; }
    .wtn-reveal-group.is-inview > *:nth-child(8)  { transition-delay: 620ms; }

    /* Medallions "settle" — slight rotation as they arrive, like they're
       being pressed into place */
    .wtn-reveal-group--medallions > * {
      transform: translate3d(0, 22px, 0) scale(0.7) rotate(-10deg);
      transform-origin: center;
    }
    .wtn-reveal-group--medallions.is-inview > * {
      transform: translate3d(0, 0, 0) scale(1) rotate(0deg);
    }

    @media (prefers-reduced-motion: reduce) {
      .wtn-reveal, .wtn-reveal-group > * {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
    }

    /* ---------- RESPONSIVE ---------- */
    @media (max-width: 960px) {
      .wtn-features__grid { grid-template-columns: repeat(2, 1fr); }
      .wtn-why__images { grid-template-columns: repeat(2, 1fr); }
      .wtn-why__grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 560px) {
      .wtn-features__grid { grid-template-columns: 1fr 1fr; gap: 16px; }
      .wtn-why__images { grid-template-columns: 1fr; }
      .wtn-why__grid { grid-template-columns: 1fr; }
      .wtn-section { padding: 44px 18px 60px; }
      .wtn-trust__inner { gap: 18px; }
      .wtn-pcard__imgwrap { height: 220px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .wtn-pcard { transition: none; }
    }
  `;

  return (
    <div className="wtn-home">
      <Helmet>
        <title>Orang Utan Organics | Authentic Himalayan Organic Products | 100% Pure</title>
        <meta name="description" content="Buy 100% authentic Himalayan organic products — Harshil Rajma, A2 Badri Cow Ghee, Forest Honey, Red Rice & more. Farm-to-table produce from Gangotri Valley. Free delivery above ₹1000." />
        <link rel="canonical" href="https://orangutanorganics.com" />
        <meta property="og:title" content="Orang Utan Organics | Authentic Himalayan Organic Products | 100% Pure" />
        <meta property="og:description" content="Buy 100% authentic Himalayan organic products — Harshil Rajma, A2 Badri Cow Ghee, Forest Honey, Red Rice & more. Farm-to-table from Gangotri Valley." />
        <meta property="og:url" content="https://orangutanorganics.com" />
        <meta property="og:image" content="https://orangutanorganics.com/newlogo.png?v=2" />
        <meta property="og:type" content="website" />
      </Helmet>
      <style>{scopedStyles}</style>

      {/* Landing-page loader — self-dismisses on window.load (max 3.5s fallback) */}
      <PageLoader />

      {/* ============ HERO — SCROLL ANIMATION ============ */}
      <div className="wtn-hs-wrapper" ref={heroScrollRef}>
        <section className="wtn-hero" ref={heroRef}>
          {/* Background images — stacked, cross-fade via JS */}
          <img ref={img1Ref}   className="wtn-hs-img" src={heroImg1}   alt="" aria-hidden="true" loading="eager" decoding="async" />
          <img ref={img2Ref}   className="wtn-hs-img" src={heroImg2}   alt="" aria-hidden="true" loading="lazy" />
          <img ref={img3Ref}   className="wtn-hs-img" src={heroImg3}   alt="" aria-hidden="true" loading="lazy" />
          <img ref={img4Ref}   className="wtn-hs-img" src={heroImg4}   alt="" aria-hidden="true" loading="lazy" />

          {/* Text banner 1 */}
          <div ref={text1Ref} className="wtn-hs-text">
            <h1 className="wtn-hs-text__head">
              Orang Utan Organics <span style={{fontWeight: 400}}>(meaning forest people)</span> brings sustainably and organically grown produce from Gangotri Valley.
            </h1>
          </div>

          {/* Text banner 2 */}
          <div ref={text2Ref} className="wtn-hs-text">
            <h2 className="wtn-hs-text__head">Altitude changes everything.</h2>
            <p className="wtn-hs-text__body">
              Grains, pulses, ghee and wild honey – grown in mountain farms situated at 4000 ft to 12000 ft altitude. High altitude = Longer maturation period = <br></br><div style={{fontWeight: 'bold'}}>
              More protein. More nutrients. More flavour.</div>
            </p>
          </div>

          {/* Text banner 3 */}
          <div ref={text3Ref} className="wtn-hs-text">
            <h2 className="wtn-hs-text__head">Nothing added.<br/>Nothing removed.</h2>
            <p className="wtn-hs-text__body">
              Unpolished, unsorted by machine, untouched by chemicals. Grown, hand-picked, and sun-dried by small holder farmers in small batches. <br></br><div style={{fontWeight: 'bold'}}>What reaches you is what the farmers picked.</div>
            </p>
          </div>

          {/* Text banner 4 */}
          <div ref={text4Ref} className="wtn-hs-text">
            <h2 className="wtn-hs-text__head">Grown by women farmers.</h2>
            <p className="wtn-hs-text__body">
              We source directly from the women farmers who use traditional practices passed down through generations — crop rotation, natural composting, no pesticides, no irrigation beyond what the mountain provides. <br></br><div style={{fontWeight: 'bold'}}>Our produce is traceable to the very hands that harvested them.</div>
            </p>
          </div>

          {/* Decorative asset images — slide in from right on scenes 2 / 3 / 4 */}
          <img ref={asset2Ref} className="wtn-hs-asset wtn-hs-asset--mid" src={heroAsset2} alt="" aria-hidden="true" loading="lazy" />
          <img ref={asset3Ref} className="wtn-hs-asset" src={heroAsset3} alt="" aria-hidden="true" loading="lazy" />
          <img ref={asset4Ref} className="wtn-hs-asset" src={heroAsset4} alt="" aria-hidden="true" loading="lazy" />

          {/* Mobile-only: tagline in the space below the mountain image */}
          <div className="wtn-hs-tagline-mobile" aria-hidden="true">
            <span className="tm-label">Searching for</span>
            <span className="tm-heading">"Himalayan" "authentic" "pure"</span>
            <span className="tm-label">Everyone says it. Nobody shows it. So we stopped asking you to trust us. Scan any pack meet the farm, the hands, the harvest.</span>
          </div>

          {/* Skip button — rendered here so it stacks in DOM, positioned fixed via CSS */}
          <button ref={skipBtnRef} className="wtn-hs-skip" aria-label="Skip intro animation">
            Skip intro ↓
          </button>
        </section>
      </div>

      {/* ============ TRUST STRIP ============ */}
      <section className="wtn-trust" aria-label="Trust markers" ref={trustRef}>
        <div className="wtn-trust__inner wtn-reveal-group wtn-reveal-group--medallions">
          <Medallion title="Base Altitude" sub="7,500 FT"      img={trustFarmAltitude} />
          <Medallion title="Grown By"      sub="Women Farmers" img={trustWomenFarmer} />
          <Medallion title="Complete"      sub="Plant Protein" img={trustPlantProtein} />
          {/* <Medallion title="Traditional"   sub="How to Cook"   img={trustHowToCook} /> */}
          <Medallion title="GI Tag"      sub="Uttarakhand" img={trustGiTag} />
          {/* <img src={trustGiTag}     alt="GI Tag Uttarakhand"        className="wtn-trust__badge" style={{ width: 72, height: 72 }} /> */}
          {/* <img src={trustAltChange} alt="Altitude changes everything" className="wtn-trust__badge" style={{ width: 82, height: 82 }} /> */}
        </div>
      </section>

      {/* ============ WHY IT MATTERS — shared-element scroll transition ============ */}
      <WhyItMattersTransition />
      <VideoTransition />
      <StaticImageSection />

      {/* wave into products (flipped so it feels like a descent) */}
      <WaveDivider height={80} palette={[H.PAPER, H.CREAM_SOFT]} flip />

      {/* ============ PRODUCTS ============ */}
      {/* NEW: horizontal pinned scroll. First 3 cards visible; strip
          scrolls left as the user scrolls the page down. Falls back to a
          vertical grid on mobile (see .wtn-hscroll media query).
          The previous vertical grid lives commented-out inside
          <HorizontalProducts /> for easy restore. */}
      <HorizontalProducts />

      {/* wave into why (descending into meadow gold) */}
      {/* <WaveDivider height={90} palette={[H.MEADOW_GOLD, H.OCHRE]} /> */}

      {/* ============ WHY ============ */}
      {/* <section className="wtn-section wtn-why">
        <div className="wtn-section__inner">
          <div style={{ marginBottom: 30 }}>
            <span className="wtn-eyebrow wtn-reveal wtn-reveal--from-left">Why Us</span>
            <h2 className="wtn-h2 wtn-reveal" style={{ marginBottom: 6 }}>Why Choose Orang Utan Organics?</h2>
            <svg className="wtn-h2-rule" viewBox="0 0 84 6" preserveAspectRatio="none" aria-hidden="true" focusable="false">
              <path d="M0,3 C18,0 38,6 56,3 C72,0 80,4 84,3" stroke={H.FOREST_SHADOW} strokeWidth="1.6" fill="none" strokeLinecap="round"/>
            </svg>
          </div>

          <div className="wtn-why__images wtn-reveal-group">
            <img src={img11} alt="Himalayan farming" className="wtn-why__img" loading="lazy" />
            <img src={img12} alt="Organic products" className="wtn-why__img" loading="lazy" />
            <img src={img13} alt="Traditional methods" className="wtn-why__img" loading="lazy" />
          </div>

          <div className="wtn-why__grid wtn-reveal-group">
            <div className="wtn-wcard">
              <h3>Rooted in the Himalayas</h3>
              <p>Every grain and drop comes from our mountain farms above 2,000 - 5,000 ft — pure soil, glacier water, and clean air. We grow slow, so your food can stay real.</p>
            </div>
            <div className="wtn-wcard">
              <h3>Farmer-First Promise</h3>
              <p>We work shoulder-to-shoulder with Himalayan farmers — not middlemen. Every purchase sustains rural families, revives native seeds, and keeps mountain livelihoods alive.</p>
            </div>
            <div className="wtn-wcard">
              <h3>Traceable &amp; Transparent</h3>
              <p>Scan a QR on your pack to trace your food back to its village, harvest batch, and farmer story. No hidden supply chain, no marketing fluff — just the truth behind your food.</p>
            </div>
            <div className="wtn-wcard">
              <h3>Crafted the Traditional Way</h3>
              <p>Bilona-churned ghee, hand-cleaned pulses, sun-dried grains — our methods are timeless because they work. We preserve nutrition and heritage in every batch.</p>
            </div>
            <div className="wtn-wcard">
              <h3>Regenerative by Design</h3>
              <p>From compost-back initiatives to low-water farming, we grow in ways that heal soil and protect the Himalayas. Sustainability isn't a buzzword here — it's survival.</p>
            </div>
            <div className="wtn-wcard">
              <h3>Pure Nutrition, No Pretence</h3>
              <p>No refined oils. No preservatives. No shortcuts. Just small-batch Himalayan food, rich in native nutrients and natural taste — the way nature intended.</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* wave into CTA (descending into deep forest) */}
      <WaveDivider height={90} palette={[H.SOIL_OLIVE, H.DEEP_FOREST, H.DEEP_FOREST_DK]}  />

      {/* ============ CTA ============ */}
      <section className="wtn-section wtn-cta">
        <div className="wtn-cta__inner wtn-reveal wtn-reveal--scale">
          <span className="wtn-cta__eyebrow">Get Started</span>
          <h2>Ready to Experience Pure Himalayan Goodness?</h2>
          <p>Start your journey to healthier living with our organic products.</p>
          <Link to="/products" className="wtn-btn wtn-btn--primary wtn-btn--large">
            Start Shopping
          </Link>
        </div>
      </section>

      {/* wave rising up from the dark CTA into the cream Instagram section */}
      <WaveDivider height={80} palette={[H.CREAM, H.CREAM_SOFT]} flip />

      {/* ============ INSTAGRAM ============ */}
      {/* Same pinned horizontal-scroll pattern as the Products section
          (see <HorizontalProducts />), applied to Instagram embeds. */}
      <HorizontalInstagram />

      {/* ============ CUSTOMER REVIEWS (marquee) ============ */}
      <ReviewMarquee />

      {/* wave descending into the dark footer */}
      <WaveDivider height={90} palette={[H.SOIL_OLIVE, H.DEEP_FOREST, H.DEEP_FOREST_DK]} />
    </div>
  );
}

function ProductCard({ product }) {
  const minPrice = Math.min(...product.variants.map(v => v.price));
  const maxPrice = Math.max(...product.variants.map(v => v.price));

  const hasProductDiscount = hasDiscount(product.name);
  const discountPercent = getDiscount(product.name);
  const minDiscountedPrice = getDiscountedPrice(minPrice, product.name);
  const maxDiscountedPrice = getDiscountedPrice(maxPrice, product.name);

  const priceDisplay = minPrice === maxPrice
    ? `₹${minPrice}`
    : `₹${minPrice} - ₹${maxPrice}`;

  const discountedPriceDisplay = minDiscountedPrice === maxDiscountedPrice
    ? `₹${minDiscountedPrice}`
    : `₹${minDiscountedPrice} - ₹${maxDiscountedPrice}`;

  const isProductBestseller = isBestseller(product.name);
  const carouselImages = product.images || [product.image];

  return (
    <Link to={`/products/${product.id}`} className="wtn-pcard">
      <div className="wtn-pcard__imgwrap">
        <ImageCarousel images={carouselImages} alt={product.name} />
        <span className="wtn-pcard__badge">{product.category}</span>
        {isProductBestseller && (
          <span className="wtn-pcard__best">Bestseller</span>
        )}
        {hasProductDiscount && DISCOUNT_CONFIG.showTag && (
          <span className="wtn-pcard__disc">
            {DISCOUNT_CONFIG.tagLabel} {discountPercent}%
          </span>
        )}
        <CardWaveCut />
      </div>
      <div className="wtn-pcard__body">
        <h3 className="wtn-pcard__name">{product.name}</h3>
        <p className="wtn-pcard__desc">{product.description.substring(0, 80)}...</p>
        <div className="wtn-pcard__benefits">
          {product.benefits.slice(0, 3).map((benefit, idx) => (
            <span key={idx} className="wtn-btag">{benefit}</span>
          ))}
        </div>
        <div className="wtn-pcard__foot">
          <div>
            {hasProductDiscount ? (
              <>
                <span className="wtn-pcard__price--strike">{priceDisplay}</span>
                <span className="wtn-pcard__price--now">{discountedPriceDisplay}</span>
              </>
            ) : (
              <span className="wtn-pcard__price">{priceDisplay}</span>
            )}
          </div>
          <span className="wtn-pcard__go">View →</span>
        </div>
      </div>
    </Link>
  );
}
// ===== NEW REDESIGN — WAVE THEME — END =====

/* ===================================================================
   HorizontalProducts
   Pinned horizontal scroll. The section is tall enough to provide
   scroll runway equal to the overflow width of the card strip. As
   the user scrolls vertically through the section, the strip is
   translated horizontally by the same amount — so it feels like the
   page scroll is driving the horizontal reveal.

   No GSAP dependency. Uses the same rAF-throttled scroll listener
   pattern as WhyItMattersTransition. On mobile (≤900px) the pinning
   is dropped and cards fall back to a horizontal-swipe strip so
   phone users still get a working experience.
================================================================== */
function HorizontalProducts() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const stripRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const strip = stripRef.current;
    if (!section || !pin || !strip) return;

    let rafId = 0;
    let horizontalScrollLength = 0;

    // Measure the sticky nav so the pin's content starts BELOW it. Without
    // this the nav overlays the pinned header (only the sub-copy peeks out).
    // Falls back to 100px if the nav isn't found.
    const measureNav = () => {
      const navEl =
        document.querySelector('.wtn-header') ||
        document.querySelector('header');
      const h = navEl ? navEl.getBoundingClientRect().height : 100;
      pin.style.setProperty('--nav-h', `${h}px`);
    };

    // Recompute the horizontal runway + total section height on mount and
    // on resize. Section height = 100vh (pin window) + horizontal overflow.
    // That gives a natural 1:1 scrub between page scroll and strip travel.
    // Applies at ALL viewport sizes — mobile uses the same pin + transform
    // as desktop, just with wider slides (per media queries in CSS).
    const refresh = () => {
      measureNav();
      horizontalScrollLength = Math.max(0, strip.scrollWidth - window.innerWidth);
      section.style.height = `calc(100vh + ${horizontalScrollLength}px)`;
      compute();
    };

    // JS-driven pinning: we don't use position:sticky because .wtn-home
    // has overflow-x:hidden which breaks sticky on descendants. Instead
    // we toggle the pin between position:absolute (before/after) and
    // position:fixed (during) based on the section's viewport position.
    // The three states are continuous — the pin is visually anchored at
    // the same on-screen y-coordinate across the boundary, so there is
    // no jump when it switches modes.
    const compute = () => {
      rafId = 0;
      if (horizontalScrollLength <= 0) return;
      const rect = section.getBoundingClientRect();

      if (rect.top >= 0) {
        // Before pin — section hasn't reached the top of the viewport yet.
        pin.style.position = 'absolute';
        pin.style.top = '0';
        pin.style.left = '0';
        pin.style.right = '0';
        strip.style.transform = 'translate3d(0, 0, 0)';
      } else if (-rect.top < horizontalScrollLength) {
        // Pinned — while scrolling within the runway, freeze pin at
        // viewport top and translate the strip by the same delta as the
        // page has scrolled past the section top.
        pin.style.position = 'fixed';
        pin.style.top = '0';
        pin.style.left = '0';
        pin.style.right = '0';
        const p = -rect.top / horizontalScrollLength;
        strip.style.transform = `translate3d(${-horizontalScrollLength * p}px, 0, 0)`;
      } else {
        // After pin — anchor pin at the bottom of the section so the next
        // section can flow naturally underneath.
        pin.style.position = 'absolute';
        pin.style.top = `${horizontalScrollLength}px`;
        pin.style.left = '0';
        pin.style.right = '0';
        strip.style.transform = `translate3d(${-horizontalScrollLength}px, 0, 0)`;
      }
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(compute);
    };

    refresh();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', refresh);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', refresh);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section ref={sectionRef} className="wtn-hscroll" aria-label="Our Products">
      <style>{HSCROLL_STYLES}</style>

      <div ref={pinRef} className="wtn-hscroll__pin">
        <div className="wtn-hscroll__head">
          <span className="wtn-eyebrow">The Harvest</span>
          <h2 className="wtn-hscroll__h2">Our Products</h2>
          <p className="wtn-hscroll__sub">
            Discover our range of authentic Himalayan organic products.
          </p>
        </div>

        <div className="wtn-hscroll__viewport">
          <div ref={stripRef} className="wtn-hscroll__strip">
            {PRODUCTS.map((product) => (
              <div key={product.id} className="wtn-hscroll__slide">
                <ProductCard product={product} />
              </div>
            ))}
            {/* Trailing CTA tile — terminator at end of strip */}
            <div className="wtn-hscroll__slide wtn-hscroll__slide--cta">
              <Link to="/products" className="wtn-hscroll__ctacard">
                <span className="wtn-hscroll__ctaEyebrow">Full Harvest</span>
                <h3 className="wtn-hscroll__ctaTitle">See every product from the valley</h3>
                <span className="wtn-btn wtn-btn--primary">View All Products →</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const HSCROLL_STYLES = `
  /* Section is a positioning context for the JS-driven pin.
     Height is set dynamically in JS to 100vh + strip overflow. */
  .wtn-hscroll {
    position: relative;
    background: ${H.PAPER};
  }
  /* Pin fills exactly one viewport. Position is toggled by JS between
     absolute/fixed/absolute so it stays anchored during horizontal scroll.
     padding-top uses --nav-h (set in JS from the real nav bar height) so
     the pinned header sits BELOW the sticky nav instead of behind it. */
  .wtn-hscroll__pin {
    position: absolute; top: 0; left: 0; right: 0;
    height: 100vh;
    display: flex; flex-direction: column;
    overflow: hidden;
    padding: calc(var(--nav-h, 100px) + 20px) 0 24px;
    box-sizing: border-box;
    background: ${H.PAPER};
  }
  /* Compact header — kept tight so header + one row of cards fits inside 100vh. */
  .wtn-hscroll__head {
    text-align: center;
    padding: 0 28px 16px;
    max-width: 1320px; margin: 0 auto;
    flex: 0 0 auto;
  }
  .wtn-hscroll__h2 {
    font-family: Georgia, "Iowan Old Style", serif;
    font-size: clamp(24px, 2.8vw, 34px);
    color: ${H.DEEP_FOREST_DK};
    margin: 10px 0 6px; line-height: 1.15;
  }
  .wtn-hscroll__sub {
    max-width: 560px; margin: 0 auto;
    color: black; opacity: 0.78;
    font-size: 14.5px; line-height: 1.55;
  }
  /* Strip viewport takes remaining vertical space and centers the cards. */
  .wtn-hscroll__viewport {
    flex: 1 1 auto;
    display: flex; align-items: center;
    overflow: hidden;
    width: 100%;
    min-height: 0;
  }
  .wtn-hscroll__strip {
    display: flex; flex-wrap: nowrap;
    align-items: stretch;
    padding-left: 4vw; padding-right: 4vw;
    will-change: transform;
  }
  /* ~3 cards visible on a typical desktop (30vw × 3 = 90vw, plus edge padding). */
  .wtn-hscroll__slide {
    flex: 0 0 30vw;
    max-width: 440px; min-width: 280px;
    padding: 0 12px;
    box-sizing: border-box;
    display: flex;
  }
  .wtn-hscroll__slide .wtn-pcard { width: 100%; }
  /* Image height derived from viewport so the whole card fits inside the
     remaining vertical space, regardless of screen height. */
  .wtn-hscroll__slide .wtn-pcard__imgwrap { height: clamp(180px, 30vh, 260px); }

  /* Trailing CTA card — same visual weight as a product card. */
  .wtn-hscroll__slide--cta { align-items: stretch; }
  .wtn-hscroll__ctacard {
    display: flex; flex-direction: column; justify-content: center;
    text-align: center; text-decoration: none;
    background: ${H.DEEP_FOREST}; color: ${H.CREAM};
    padding: 28px 24px;
    border: 1px solid ${H.DEEP_FOREST_DK};
    transition: transform 220ms ease, background 220ms ease;
    width: 100%;
  }
  .wtn-hscroll__ctacard:hover { transform: translateY(-4px); background: ${H.DEEP_FOREST_DK}; }
  .wtn-hscroll__ctaEyebrow {
    display: inline-block; align-self: center;
    background: ${H.MEADOW_GOLD}; color: ${H.FOREST_SHADOW};
    font-size: 10.5px; letter-spacing: 0.28em; text-transform: uppercase;
    font-weight: 800; padding: 6px 12px; margin-bottom: 16px;
  }
  .wtn-hscroll__ctaTitle {
    font-family: Georgia, "Iowan Old Style", serif;
    font-size: clamp(20px, 2vw, 26px);
    color: ${H.CREAM}; line-height: 1.2;
    margin: 0 0 20px;
  }
  .wtn-hscroll__ctacard .wtn-btn { align-self: center; }

  /* -------- MOBILE / TABLET --------
     Same pin + JS-driven horizontal scroll as desktop. We only tune slide
     width and image height so the cards read well on smaller screens. */
  @media (max-width: 900px) {
    .wtn-hscroll__pin { padding-top: calc(var(--nav-h, 100px) + 14px); }
    .wtn-hscroll__head { padding: 0 20px 12px; }
    .wtn-hscroll__h2 { font-size: clamp(22px, 4.4vw, 28px); margin: 6px 0 4px; }
    .wtn-hscroll__sub { font-size: 13.5px; }
    .wtn-hscroll__strip {
      padding-left: 6vw; padding-right: 6vw;
    }
    .wtn-hscroll__slide {
      flex: 0 0 62vw;
      max-width: none; min-width: 0;
      padding: 0 8px;
    }
    .wtn-hscroll__slide .wtn-pcard__imgwrap { height: clamp(160px, 22vh, 220px); }
  }
  @media (max-width: 560px) {
    .wtn-hscroll__slide { flex: 0 0 76vw; padding: 0 6px; }
    .wtn-hscroll__slide .wtn-pcard__imgwrap { height: clamp(150px, 20vh, 200px); }
  }
`;

/* ===================================================================
   HorizontalInstagram
   Same pinned horizontal-scroll pattern as <HorizontalProducts /> but
   showing Instagram embeds. Cards are iframes; iframe height is clamped
   so the header + one row of cards fits inside 100vh across viewports.
================================================================== */
function HorizontalInstagram() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const stripRef = useRef(null);
  const [embedsReady, setEmbedsReady] = useState(false);

  // Load iframes only once the section scrolls into view — prevents Instagram's
  // 30+ scripts from blocking the initial page load.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEmbedsReady(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const strip = stripRef.current;
    if (!section || !pin || !strip) return;

    let rafId = 0;
    let horizontalScrollLength = 0;

    const measureNav = () => {
      const navEl =
        document.querySelector('.wtn-header') ||
        document.querySelector('header');
      const h = navEl ? navEl.getBoundingClientRect().height : 100;
      pin.style.setProperty('--nav-h', `${h}px`);
    };

    const refresh = () => {
      measureNav();
      horizontalScrollLength = Math.max(0, strip.scrollWidth - window.innerWidth);
      section.style.height = `calc(100vh + ${horizontalScrollLength}px)`;
      compute();
    };

    const compute = () => {
      rafId = 0;
      if (horizontalScrollLength <= 0) return;
      const rect = section.getBoundingClientRect();

      if (rect.top >= 0) {
        pin.style.position = 'absolute';
        pin.style.top = '0';
        pin.style.left = '0';
        pin.style.right = '0';
        strip.style.transform = 'translate3d(0, 0, 0)';
      } else if (-rect.top < horizontalScrollLength) {
        pin.style.position = 'fixed';
        pin.style.top = '0';
        pin.style.left = '0';
        pin.style.right = '0';
        const p = -rect.top / horizontalScrollLength;
        strip.style.transform = `translate3d(${-horizontalScrollLength * p}px, 0, 0)`;
      } else {
        pin.style.position = 'absolute';
        pin.style.top = `${horizontalScrollLength}px`;
        pin.style.left = '0';
        pin.style.right = '0';
        strip.style.transform = `translate3d(${-horizontalScrollLength}px, 0, 0)`;
      }
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(compute);
    };

    refresh();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', refresh);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', refresh);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section ref={sectionRef} className="wtn-ihs" aria-label="Instagram feed">
      <style>{HSCROLL_INSTA_STYLES}</style>

      <div ref={pinRef} className="wtn-ihs__pin">
        <div className="wtn-ihs__head">
          <span className="wtn-eyebrow">Follow the Journey</span>
          <h2 className="wtn-ihs__h2">From the Farms, To Your Feed.</h2>
          <p className="wtn-ihs__sub">
            Peek into life at 7,500 ft — harvests, the women who farm them, and the moments in between.
          </p>
        </div>

        <div className="wtn-ihs__viewport">
          <div ref={stripRef} className="wtn-ihs__strip">
            {INSTAGRAM_POSTS.map((url, i) => {
              const embed = toInstagramEmbed(url);
              if (!embed) return null;
              return (
                <div key={i} className="wtn-ihs__slide">
                  <div className="wtn-ihs__card">
                    {embedsReady ? (
                      <iframe
                        src={embed}
                        title={`Instagram post ${i + 1}`}
                        loading="lazy"
                        frameBorder="0"
                        scrolling="no"
                        allowTransparency="true"
                        allow="encrypted-media"
                      />
                    ) : (
                      <div className="wtn-ihs__placeholder" aria-hidden="true" />
                    )}
                    {/* Wavy scallop at the bottom of the card — matches the video-frame theme */}
                    <svg className="wtn-ihs__cardwave" viewBox="0 0 400 24" preserveAspectRatio="none"
                         aria-hidden="true" focusable="false">
                      <path d="M0,12 C80,0 160,20 220,10 C280,0 340,18 400,8 L400,24 L0,24 Z" fill="#fff"/>
                      <path d="M0,14 C80,2 160,22 220,12 C280,2 340,20 400,10"
                            stroke={H.GOLD_LINE} strokeWidth="1" fill="none" opacity="0.55"/>
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const HSCROLL_INSTA_STYLES = `
  .wtn-ihs {
    position: relative;
    background: ${H.CREAM};
  }
  .wtn-ihs__pin {
    position: absolute; top: 0; left: 0; right: 0;
    height: 100vh;
    display: flex; flex-direction: column;
    overflow: hidden;
    padding: calc(var(--nav-h, 100px) + 20px) 0 24px;
    box-sizing: border-box;
    background: ${H.CREAM};
  }
  .wtn-ihs__head {
    text-align: center;
    padding: 0 28px 16px;
    max-width: 1320px; margin: 0 auto;
    flex: 0 0 auto;
  }
  .wtn-ihs__h2 {
    font-family: Georgia, "Iowan Old Style", serif;
    font-size: clamp(24px, 2.8vw, 34px);
    color: ${H.DEEP_FOREST_DK};
    margin: 10px 0 6px; line-height: 1.15;
  }
  .wtn-ihs__sub {
    max-width: 560px; margin: 0 auto;
    color: black; opacity: 0.78;
    font-size: 14.5px; line-height: 1.55;
  }
  .wtn-ihs__viewport {
    flex: 1 1 auto;
    display: flex; align-items: center;
    overflow: hidden;
    width: 100%;
    min-height: 0;
  }
  .wtn-ihs__strip {
    display: flex; flex-wrap: nowrap;
    align-items: stretch;
    padding-left: 4vw; padding-right: 4vw;
    will-change: transform;
  }
  .wtn-ihs__slide {
    flex: 0 0 28vw;
    max-width: 400px; min-width: 300px;
    padding: 0 12px;
    box-sizing: border-box;
    display: flex;
  }
  .wtn-ihs__card {
    position: relative;
    background: #fff;
    border: 1.5px solid ${H.DEEP_FOREST};
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(1, 53, 50, 0.10);
    width: 100%;
    display: flex;
    /* Padding around the iframe so the embed doesn't touch the card edges. */
    padding: 10px 10px 24px;
    box-sizing: border-box;
  }
  .wtn-ihs__card iframe {
    display: block;
    width: 100%;
    /* Sized to fit the available pin space (100vh minus nav + header + padding),
       capped at 680px on tall viewports. Prevents align-items:center from
       cropping the top/bottom of the embed. */
    height: min(calc(100vh - 360px), 660px);
    border: 0;
  }
  .wtn-ihs__placeholder {
    display: block;
    width: 100%;
    height: min(calc(100vh - 360px), 660px);
    background: linear-gradient(90deg, #f0ebe0 25%, #e8e0d0 50%, #f0ebe0 75%);
    background-size: 200% 100%;
    animation: wtn-ihs-shimmer 1.6s ease-in-out infinite;
    border-radius: 4px;
  }
  @keyframes wtn-ihs-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  .wtn-ihs__cardwave {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    display: block;
    width: 100%;
    height: 22px;
    pointer-events: none;
    z-index: 2;
  }

  /* ------- MOBILE / TABLET -------
     Same pin + JS-driven horizontal scroll as desktop. We only tune slide
     width, header padding, and iframe height so cards read well on smaller
     screens. Iframe height uses the same min(calc, max) fit pattern with
     mobile-appropriate overhead assumptions so top/bottom don't crop. */
  @media (max-width: 900px) {
    .wtn-ihs__pin { padding-top: calc(var(--nav-h, 100px) + 14px); }
    .wtn-ihs__head { padding: 0 20px 12px; }
    .wtn-ihs__h2 { font-size: clamp(22px, 4.4vw, 28px); margin: 6px 0 4px; }
    .wtn-ihs__sub { font-size: 13.5px; }
    .wtn-ihs__strip { padding-left: 6vw; padding-right: 6vw; }
    .wtn-ihs__slide {
      flex: 0 0 62vw;
      max-width: none; min-width: 0;
      padding: 0 8px;
    }
    /* Crop the iframe so it ends around "View more on Instagram",
       hiding Instagram's like-count / suggested-posts strip that appears below. */
    .wtn-ihs__card iframe,
    .wtn-ihs__placeholder { height: min(calc(100vh - 280px), 500px); }
  }
  @media (max-width: 560px) {
    .wtn-ihs__slide { flex: 0 0 76vw; padding: 0 6px; }
    .wtn-ihs__card iframe,
    .wtn-ihs__placeholder { height: min(calc(100vh - 260px), 480px); }
  }
`;

/* ===================================================================
   ReviewMarquee
   Seamless left→right infinite marquee of customer review cards.
   Design language mirrors the Customer Reviews section in Products.js:
   PAPER background, CREAM card, DEEP_FOREST avatar, Georgia serif name,
   MEADOW_GOLD uppercase location, SOIL_OLIVE source line, star rating
   from the shared <StarRating> component.

   To seamlessly loop, the reviews array is duplicated in the DOM and the
   track animates from translateX(-50%) → translateX(0) (i.e., left-to-right
   scroll). After one full loop the track is visually identical to its
   start, so there's no jump.
================================================================== */
function ReviewMarquee() {
  const doubled = [...CUSTOMER_REVIEWS, ...CUSTOMER_REVIEWS];
  return (
    <section className="wtn-rvm" aria-label="Customer Reviews">
      <style>{REVIEW_MARQUEE_STYLES}</style>
      <div className="wtn-rvm__head">
        <h2 className="wtn-rvm__title">Customer Reviews</h2>
        <p className="wtn-rvm__sub">
          Real words from the kitchens using our Himalayan harvest.
        </p>
      </div>
      <div className="wtn-rvm__viewport">
        <div className="wtn-rvm__track">
          {doubled.map((r, i) => (
            <article key={i} className="wtn-rvm__card" aria-hidden={i >= CUSTOMER_REVIEWS.length}>
              <div className="wtn-rvm__row">
                <div className="wtn-rvm__author">
                  <div className="wtn-rvm__avatar">{r.name.charAt(0).toUpperCase()}</div>
                  <div>
                    <h4 className="wtn-rvm__name">{r.name}</h4>
                    <span className="wtn-rvm__loc">{r.location}</span>
                  </div>
                </div>
                <StarRating rating={r.rating} showCount={false} size="small" />
              </div>
              <p className="wtn-rvm__text">{r.review}</p>
              {r.source && (
                <span className="wtn-rvm__source">Verified purchase — {r.source}</span>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const REVIEW_MARQUEE_STYLES = `
  .wtn-rvm {
    background: ${H.PAPER};
    padding: 72px 0 88px;
    overflow: hidden;
  }
  .wtn-rvm__head {
    max-width: 900px; margin: 0 auto 32px;
    text-align: center; padding: 0 28px;
  }
  .wtn-rvm__title {
    font-family: Georgia, "Iowan Old Style", serif;
    font-size: clamp(24px, 3vw, 34px);
    color: ${H.DEEP_FOREST_DK};
    margin: 0 0 8px; font-weight: 700;
  }
  .wtn-rvm__sub {
    color: black; opacity: 0.78;
    font-size: 15px; line-height: 1.55;
    margin: 0;
  }

  .wtn-rvm__viewport {
    position: relative;
    overflow: hidden;
    /* Edge fade so cards smoothly appear/disappear at the sides */
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 72px, #000 calc(100% - 72px), transparent 100%);
            mask-image: linear-gradient(90deg, transparent 0, #000 72px, #000 calc(100% - 72px), transparent 100%);
  }
  .wtn-rvm__track {
    display: flex;
    gap: 20px;
    width: max-content;
    padding: 8px 20px;
    /* Change --rvm-speed to tune the marquee tempo (larger = slower). */
    animation: wtn-rvm-scroll var(--rvm-speed, 55s) linear infinite;
  }
  .wtn-rvm__viewport:hover .wtn-rvm__track {
    animation-play-state: paused;
  }
  /* Left-to-right direction: content slides rightward. To reverse into a
     traditional right-to-left marquee, swap the from/to values below. */
  @keyframes wtn-rvm-scroll {
    from { transform: translate3d(-50%, 0, 0); }
    to   { transform: translate3d(0, 0, 0); }
  }

  .wtn-rvm__card {
    flex: 0 0 340px;
    background: ${H.CREAM};
    border: 1px solid rgba(3,96,92,0.12);
    padding: 22px 24px;
    display: flex; flex-direction: column;
  }
  .wtn-rvm__row {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 12px; margin-bottom: 12px;
    flex-wrap: wrap;
  }
  .wtn-rvm__author { display: flex; align-items: center; gap: 12px; }
  .wtn-rvm__avatar {
    width: 40px; height: 40px;
    background: ${H.DEEP_FOREST}; color: ${H.CREAM};
    border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 15px;
    flex-shrink: 0;
  }
  .wtn-rvm__name {
    margin: 0;
    font-family: Georgia, "Iowan Old Style", serif;
    font-size: 15px; color: ${H.DEEP_FOREST_DK}; font-weight: 700;
    line-height: 1.2;
  }
  .wtn-rvm__loc {
    display: block;
    font-size: 11.5px; letter-spacing: 0.12em; text-transform: uppercase;
    color: ${H.MEADOW_GOLD}; font-weight: 700;
    margin-top: 3px;
  }
  .wtn-rvm__text {
    margin: 0 0 10px;
    font-size: 14.5px; line-height: 1.65;
    color: ${H.INK};
    /* Clamp to keep card heights consistent across varying review lengths */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
            line-clamp: 5;
    overflow: hidden;
  }
  .wtn-rvm__source {
    margin-top: auto;
    font-size: 11.5px; letter-spacing: 0.14em; text-transform: uppercase;
    font-weight: 700; color: ${H.SOIL_OLIVE};
  }

  @media (max-width: 900px) {
    .wtn-rvm { padding: 56px 0 68px; }
    .wtn-rvm__card { flex: 0 0 280px; padding: 18px 20px; }
  }
  @media (max-width: 560px) {
    .wtn-rvm__track { gap: 14px; padding: 6px 14px; }
    .wtn-rvm__card { flex: 0 0 68vw; max-width: 260px; padding: 14px 16px; }
    .wtn-rvm__avatar { width: 34px; height: 34px; font-size: 13px; }
    .wtn-rvm__name { font-size: 14px; }
    .wtn-rvm__loc { font-size: 10.5px; }
    .wtn-rvm__text { font-size: 13.5px; -webkit-line-clamp: 4; line-clamp: 4; }
    .wtn-rvm__source { font-size: 10.5px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .wtn-rvm__track { animation: none; transform: translate3d(0, 0, 0); }
  }
`;

/* ===================================================================
   WhyItMattersTransition
   Two sections, one shared <img> (position:fixed) that:
     1) RISES up from below the fold as the hero scrolls into view
        (image top follows heroRect.top from vh → 0)
     2) SHRINKS + slides into an invisible target rect in the next
        section (visible on screen the whole time)
     3) After landing, keeps tracking the target's live rect so it
        scrolls naturally with the page.
================================================================== */
function WhyItMattersTransition() {
  const heroRef = useRef(null);
  const targetRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const heroTextRef = useRef(null);

  useEffect(() => {
    const easeInOutCubic = (x) =>
      x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

    let rafId = 0;
    const compute = () => {
      rafId = 0;
      const hero = heroRef.current;
      const target = targetRef.current;
      const img = imgRef.current;
      if (!hero || !target || !img) return;

      const heroRect = hero.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const heroH = hero.offsetHeight;

      let top, left, w, h, radius, opacity, overlayOp, textOp;

      if (heroRect.top >= vh) {
        // Pre-rise: hero fully below the fold — image off-screen at bottom
        top = vh; left = 0; w = vw; h = vh;
        radius = 0; opacity = 0; overlayOp = 0; textOp = 0;
      } else if (heroRect.top > 0) {
        // RISE — image slides up from below with the hero itself
        const p = 1 - heroRect.top / vh; // 0 → 1
        top = heroRect.top;
        left = 0; w = vw; h = vh;
        radius = 0;
        opacity = 1;
        overlayOp = 0.45 * p;
        textOp = p; // heading fades in as the image rises
      } else if (heroRect.top > -heroH) {
        // SHRINK — hero scrolls out; image morphs toward the live target
        const p = -heroRect.top / heroH; // 0 → 1
        const t = easeInOutCubic(p);
        // Predict target position at p=1 (scroll runway remaining)
        const scrollRemaining = (1 - p) * heroH;
        const endTop  = targetRect.top - scrollRemaining;
        const endLeft = targetRect.left;
        const endW    = targetRect.width;
        const endH    = targetRect.height;
        top  = 0 + endTop  * t;
        left = 0 + endLeft * t;
        w    = vw + (endW - vw) * t;
        h    = vh + (endH - vh) * t;
        radius = 16 * t;
        opacity = 1;
        overlayOp = 0.45 * (1 - t);
        textOp = Math.max(0, 1 - t * 2.4);
      } else {
        // POST-shrink: image locked to the live target rect (scrolls with page)
        top = targetRect.top;
        left = targetRect.left;
        w = targetRect.width;
        h = targetRect.height;
        radius = 16;
        opacity = 1; overlayOp = 0; textOp = 0;
        // Graceful fade-out once the target scrolls above the viewport
        if (targetRect.bottom < 120) {
          opacity = Math.max(0, targetRect.bottom / 120);
        }
      }

      img.style.opacity = String(opacity);
      img.style.top    = `${top}px`;
      img.style.left   = `${left}px`;
      img.style.width  = `${w}px`;
      img.style.height = `${h}px`;
      img.style.borderRadius = `${radius}px`;

      const overlay = overlayRef.current;
      if (overlay) {
        overlay.style.opacity = String(opacity * overlayOp);
        overlay.style.top    = `${top}px`;
        overlay.style.left   = `${left}px`;
        overlay.style.width  = `${w}px`;
        overlay.style.height = `${h}px`;
        overlay.style.borderRadius = `${radius}px`;
      }

      const heroText = heroTextRef.current;
      if (heroText) {
        const finalTextOp = opacity * textOp;
        heroText.style.opacity = String(finalTextOp);
        heroText.style.top    = `${top}px`;
        heroText.style.left   = `${left}px`;
        heroText.style.width  = `${w}px`;
        heroText.style.height = `${h}px`;
        heroText.style.visibility = finalTextOp < 0.01 ? 'hidden' : 'visible';
      }
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <style>{WIM_STYLES}</style>

      {/* Section 1 — hero. 100vh, empty; the shared image (below) rises
          up to fill it as the user scrolls in. */}
      <section ref={heroRef} className="wim-hero" aria-label="Why it matters" />

      {/* Section 2 — split layout. Text on the left, invisible target
          rect on the right marks where the shared image lands. */}
      <section className="wim-next" aria-label="Why it matters — detail">
        <div className="wim-next__inner">
          <div className="wim-next__text">
            <span className="wim-next__eyebrow">Why It Matters</span>
            <h2 className="wim-next__title">Meet our Women Farmers</h2>
            <p className="wim-next__body">
              Every grain, every pulse, every drop of ghee is grown above 2,000 - 5,000 ft in
              the pristine Himalayas by farmers we know by name. 
            </p>
          </div>
          <div ref={targetRef} className="wim-next__target" aria-hidden="true" />
        </div>





      </section>

      {/* Shared fixed image — the single element that travels */}
      <img
        ref={imgRef}
        src={whyImg}
        alt="Himalayan farmland"
        className="wim-shared"
        loading="eager"
        decoding="async"
      />
      <div ref={overlayRef} className="wim-shared__overlay" aria-hidden="true" />
      <div ref={heroTextRef} className="wim-shared__text" aria-hidden="true">
        <span className="wim-shared__eyebrow">Why It Matters</span>
        <h2 className="wim-shared__title">Meet our Women Farmers</h2>
      </div>
    </>
  );
}

const WIM_STYLES = `
  .wim-hero {
    position: relative;
    height: 100vh;
    background: ${H.CREAM};
  }

  .wim-next {
    position: relative;
    background: ${H.CREAM_SOFT};
    padding: 0 6vw;
    box-sizing: border-box;
    display: flex; flex-direction: column;
    z-index: 1;
  }
  /* The split view fills the first viewport of wim-next so the target
     rect (right column) sits at true vertical center of the screen when
     the user lands here. */
  .wim-next__inner {
    display: flex; align-items: center; gap: 60px;
    max-width: 1320px; margin: 0 auto; width: 100%;
    padding: 80px 0;
  }
  .wim-next__text {
    flex: 1 1 55%;
    max-width: 620px;
  }
  .wim-next__eyebrow {
    display: inline-block;
    font-size: 13px; letter-spacing: 0.28em; text-transform: uppercase;
    font-weight: 700; color: ${H.MEADOW_GOLD};
    margin-bottom: 18px;
  }
  .wim-next__title {
    font-family: Georgia, "Playfair Display", serif;
    font-size: clamp(30px, 3.4vw, 52px);
    line-height: 1.08; font-weight: 700;
    color: ${H.DEEP_FOREST};
    margin: 0 0 22px;
  }
  .wim-next__body {
    font-size: clamp(15px, 1.05vw, 17px);
    line-height: 1.7;
    color: black;
    margin: 0;
  }
  /* Invisible landing rect — sets where the shared image ends up. */
  .wim-next__target {
    flex: 0 0 clamp(280px, 38vw, 560px);
    aspect-ratio: 4 / 3;
    /* Kept invisible; its box defines the final position of the shared image. */
    visibility: hidden;
  }

  .wim-next__features {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 28px;
    max-width: 1320px; margin: 0 auto; width: 100%;
    padding: 40px 0 100px;
  }
  .wim-next__features .wtn-feature { text-align: center; }

  /* Shared elements — all position: fixed, animated by JS via top/left/w/h */
  .wim-shared,
  .wim-shared__overlay,
  .wim-shared__text {
    position: fixed;
    top: 0; left: 0;
    width: 100vw; height: 100vh;
    pointer-events: none;
    will-change: top, left, width, height, opacity, border-radius;
    contain: layout paint;
  }
  .wim-shared {
    object-fit: cover;
    z-index: 40;
    display: block;
    opacity: 0;
    backface-visibility: hidden;
    overflow: hidden;
  }
  .wim-shared__overlay {
    z-index: 41;
    background: linear-gradient(180deg, rgba(1,53,50,0.30) 0%, rgba(1,53,50,0.55) 100%);
    opacity: 0;
  }
  .wim-shared__text {
    z-index: 42;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 0 6vw;
    color: ${H.CREAM};
    opacity: 0; visibility: hidden;
  }
  .wim-shared__eyebrow {
    display: inline-block;
    font-size: 14px; letter-spacing: 0.28em; text-transform: uppercase;
    font-weight: 700; color: ${H.MEADOW_GOLD};
    margin-bottom: 20px;
    text-shadow: 0 2px 10px rgba(0,0,0,0.35);
  }
  .wim-shared__title {
    font-family: Georgia, "Playfair Display", serif;
    font-size: clamp(38px, 6.2vw, 88px);
    line-height: 1.05; font-weight: 700;
    color: ${H.CREAM};
    margin: 0; max-width: 1100px;
    text-shadow: 0 4px 24px rgba(0,0,0,0.45);
  }

  @media (max-width: 900px) {
    /* Fill the viewport and center the text+image group so the shrunk
       image lands at the vertical middle of the section (mirrors desktop). */
    .wim-next { padding: 0 6vw; }
    .wim-next__inner {
      flex-direction: column; align-items: stretch;
      justify-content: center;
      gap: 24px;
    }
    /* Override desktop's flex: 1 1 55% (which in column mode would make
       the text take 55% of the container HEIGHT, opening a huge gap). */
    .wim-next__text { max-width: 100%; flex: 0 0 auto; }
    .wim-next__target {
      flex: 0 0 auto; align-self: center;
      width: 82vw; aspect-ratio: 4/3;
    }
    .wim-next__features {
      grid-template-columns: repeat(2, 1fr);
      gap: 22px;
      padding: 32px 0 60px;
    }
  }
  @media (max-width: 560px) {
    .wim-next { padding: 0 5vw; }
    .wim-next__inner { gap: 20px; }
    .wim-next__target { width: 88vw; }
    .wim-next__features { grid-template-columns: 1fr; padding: 24px 0 56px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .wim-shared, .wim-shared__overlay, .wim-shared__text {
      transition: none;
    }
  }
`;

/* ===================================================================
   VideoTransition
   Reverse of WhyItMattersTransition: video starts in the LEFT side of
   a split layout and expands to full-screen as the user scrolls.
   GSAP ScrollTrigger drives both the position morph and video scrubbing.
   First 18s of the 24s video play fast (75% of scroll); last 6s slow.
================================================================== */
function VideoTransition() {
  const scrollRef  = useRef(null);
  const targetRef  = useRef(null);
  const videoRef   = useRef(null);
  const dimRef     = useRef(null);
  const overlayRef = useRef(null);
  const textRef    = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    if (window.innerWidth <= 640) return;

    const eioC = (x) => x < 0.5 ? 4*x*x*x : 1 - Math.pow(-2*x+2,3)/2;
    const lerp = (a, b, t) => a + (b - a) * t;

    let rafId = 0;

    const compute = () => {
      rafId = 0;
      const section = scrollRef.current;
      const target  = targetRef.current;
      const vid     = videoRef.current;
      const dim     = dimRef.current;
      const overlay = overlayRef.current;
      const text    = textRef.current;
      if (!section || !target || !vid) return;

      const sr = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      // Section fully outside viewport — hide all fixed layers + reset transforms
      if (sr.top > vh || sr.bottom < 0) {
        vid.style.opacity       = '0';
        vid.style.transform     = '';
        dim.style.opacity       = '0';
        dim.style.transform     = '';
        overlay.style.opacity   = '0';
        overlay.style.transform = '';
        return;
      }

      const sH = section.offsetHeight;

      // Section has two phases:
      //   Animation (p 0→1): rawScroll 0 → sH-vh
      //   Exit             : rawScroll sH-vh → sH (exit ends exactly when section is gone)
      const exitRoom  = vh;
      const animRoom  = sH - vh;          // exit ends at sH, no dead-scroll gap
      const rawScroll = Math.max(0, -sr.top);

      const p     = Math.max(0, Math.min(1, rawScroll / animRoom));
      const exitP = Math.max(0, Math.min(1, (rawScroll - animRoom) / exitRoom));

      // Live target rect — follows the sticky inner at every scroll position,
      // so the video is correctly placed both before and after sticky kicks in.
      const tr = target.getBoundingClientRect();

      // 1. Position: target rect → fullscreen over first 30% of scroll
      const posT = eioC(Math.min(1, p / 0.30));
      vid.style.opacity     = '1';
      vid.style.top          = `${lerp(tr.top,    0,  posT)}px`;
      vid.style.left         = `${lerp(tr.left,   0,  posT)}px`;
      vid.style.width        = `${lerp(tr.width,  vw, posT)}px`;
      vid.style.height       = `${lerp(tr.height, vh, posT)}px`;
      vid.style.borderRadius = `${lerp(16, 0, posT)}px`;

      // 2. Right-side text fades out
      text.style.opacity = String(Math.max(0, 1 - p / 0.35));

      // 3. Dark dim fades in
      dim.style.opacity = String(Math.max(0, Math.min(0.45, ((p - 0.20) / 0.25) * 0.45)));

      // 4. Overlay text fades in
      overlay.style.opacity = String(Math.max(0, Math.min(1, (p - 0.60) / 0.20)));

      // 5. Non-linear video scrub
      //    first 18s → fast (plays over 75% of scroll distance)
      //    last  6s  → slow (plays over 25% of scroll distance)
      const t = p < 0.75
        ? (p / 0.75) * 18
        : 18 + ((p - 0.75) / 0.25) * 6;
      try {
        if (vid.fastSeek) vid.fastSeek(t);
        else vid.currentTime = t;
      } catch (_) {}

      // 6. Exit phase: slide the fullscreen video + overlays up off-screen
      //    The next section becomes visible from below as exitP goes 0 → 1.
      const exitY = -exitP * vh;
      const exitStr = exitP > 0 ? `translateY(${exitY}px)` : '';
      vid.style.transform     = exitStr;
      dim.style.transform     = exitStr;
      overlay.style.transform = exitStr;
      // Fade text overlay and dim out during exit (video itself stays opaque)
      if (exitP > 0) {
        dim.style.opacity     = String(0.45 * (1 - exitP));
        overlay.style.opacity = String(1 - exitP);
      }
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <style>{VTR_STYLES}</style>

      {/* Desktop / tablet: sticky split layout + JS-driven fixed video */}
      <section ref={scrollRef} className="vtr-scroll" aria-label="We Bring Himalayan Produce from 5,000 - 12,000 ft">
        <div className="vtr-inner">
          <div ref={targetRef} className="vtr-target" aria-hidden="true" />
          <div ref={textRef} className="vtr-text">
            <span className="vtr-text__eyebrow">Why It Matters</span>
            <h2 className="vtr-text__title">We Bring Himalayan Produce from 5,000 - 12,000 ft</h2>
            <p className="vtr-text__body">Up here, cold nights and thin air make everything grow slow. Slow means more flavour, more nutrients, and food that tastes like where it came from.</p>
          </div>
        </div>
      </section>

      <video
        ref={videoRef}
        className="vtr-video"
        src={vidHomePg}
        muted playsInline preload="auto"
        aria-hidden="true"
      />
      <div ref={dimRef}     className="vtr-dim"     aria-hidden="true" />
      <div ref={overlayRef} className="vtr-overlay" aria-hidden="true">
        <span className="vtr-overlay__eyebrow">Why It Matters</span>
        <h2  className="vtr-overlay__title">We Bring Himalayan Produce from 5,000 - 12,000 ft</h2>
      </div>

      {/* Mobile: static auto-play video card above text */}
      <div className="vtr-mobile-block" aria-label="We Bring Himalayan Produce from 5,000 - 12,000 ft">
        <div>
          <span className="vtr-mobile__eyebrow">Why It Matters</span>
          <h2  className="vtr-mobile__title">We Bring Himalayan Produce from 5,000 - 12,000 ft</h2>
          <p   className="vtr-mobile__body">Up here, cold nights and thin air make everything grow slow. Slow means more flavour, more nutrients, and food that tastes like where it came from.</p>
        </div>
        <video
          className="vtr-mobile__video"
          src={vidHomePg}
          autoPlay muted loop playsInline preload="metadata"
          aria-hidden="true"
        />
      </div>
    </>
  );
}

const VTR_STYLES = `
  /* 501vh = 500vh animation + 1vh exit — exit ends exactly when section scrolls off */
  .vtr-scroll {
    position: relative;
    height: 501vh;
    background: transparent;
  }
  .vtr-inner {
    position: sticky;
    top: 0; height: 100vh;
    overflow: hidden;
    display: flex; align-items: center; gap: 60px;
    padding: 0 6vw; box-sizing: border-box;
    background: ${H.CREAM_SOFT};
  }

  .vtr-target {
    flex: 0 0 clamp(280px, 38vw, 560px);
    aspect-ratio: 4 / 3;
    visibility: hidden;
    border-radius: 16px;
  }

  .vtr-text        { flex: 1 1 55%; max-width: 620px; }
  .vtr-text__eyebrow {
    display: inline-block;
    font-size: 13px; letter-spacing: 0.28em; text-transform: uppercase;
    font-weight: 700; color: ${H.MEADOW_GOLD}; margin-bottom: 18px;
  }
  .vtr-text__title {
    font-family: Georgia, "Playfair Display", serif;
    font-size: clamp(30px, 3.4vw, 52px);
    line-height: 1.08; font-weight: 700;
    color: ${H.DEEP_FOREST}; margin: 0 0 22px;
  }
  .vtr-text__body {
    font-size: clamp(15px, 1.05vw, 17px);
    line-height: 1.7; color: ${H.INK}; margin: 0;
  }

  /* Fixed video — driven by JS */
  .vtr-video {
    position: fixed;
    object-fit: cover; z-index: 50;
    pointer-events: none; opacity: 0;
    will-change: top, left, width, height, border-radius, opacity, transform;
  }

  /* Dark overlay — same gradient as wim-shared__overlay */
  .vtr-dim {
    position: fixed; top: 0; left: 0;
    width: 100vw; height: 100vh; z-index: 51;
    background: linear-gradient(180deg, rgba(1,53,50,0.30) 0%, rgba(1,53,50,0.55) 100%);
    opacity: 0; pointer-events: none;
    will-change: opacity, transform;
  }

  /* Full-screen text overlay */
  .vtr-overlay {
    position: fixed; top: 0; left: 0;
    width: 100vw; height: 100vh; z-index: 52;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 0 6vw;
    opacity: 0; pointer-events: none;
    will-change: opacity, transform;
  }
  .vtr-overlay__eyebrow {
    display: inline-block;
    font-size: 14px; letter-spacing: 0.28em; text-transform: uppercase;
    font-weight: 700; color: ${H.MEADOW_GOLD}; margin-bottom: 20px;
    text-shadow: 0 2px 10px rgba(0,0,0,0.35);
  }
  .vtr-overlay__title {
    font-family: Georgia, "Playfair Display", serif;
    font-size: clamp(38px, 6.2vw, 88px);
    line-height: 1.05; font-weight: 700;
    color: ${H.CREAM}; margin: 0; max-width: 1100px;
    text-shadow: 0 4px 24px rgba(0,0,0,0.45);
  }

  /* Mobile: hidden by default (shown only ≤640px) */
  .vtr-mobile-block { display: none; }

  /* ── Tablet ≤900px: stacked layout, animation still plays ── */
  @media (max-width: 900px) {
    .vtr-inner {
      flex-direction: column; align-items: stretch;
      justify-content: center; gap: 24px;
    }
    .vtr-target {
      flex: 0 0 auto; align-self: center;
      width: 82vw; aspect-ratio: 4/3;
    }
    .vtr-text       { flex: 0 0 auto; max-width: 100%; }
    .vtr-text__body { font-size: clamp(14px, 2.2vw, 17px); }
  }

  /* ── Mobile ≤640px: static layout, no animation ── */
  @media (max-width: 640px) {
    .vtr-scroll { display: none; }
    .vtr-video, .vtr-dim, .vtr-overlay { display: none; }

    .vtr-mobile-block {
      display: flex; flex-direction: column;
      gap: 0; background: ${H.CREAM_SOFT};
      padding: 56px 6vw 72px;
    }
    .vtr-mobile__video {
      width: 100%; aspect-ratio: 4/3;
      object-fit: cover; border-radius: 16px;
      display: block; margin-top: 28px;
    }
    .vtr-mobile__eyebrow {
      display: inline-block;
      font-size: 12px; letter-spacing: 0.28em; text-transform: uppercase;
      font-weight: 700; color: ${H.MEADOW_GOLD}; margin-bottom: 14px;
    }
    .vtr-mobile__title {
      font-family: Georgia, "Playfair Display", serif;
      font-size: clamp(26px, 7vw, 36px);
      line-height: 1.1; font-weight: 700;
      color: ${H.DEEP_FOREST}; margin: 0 0 16px;
    }
    .vtr-mobile__body {
      font-size: clamp(14px, 3.8vw, 16px);
      line-height: 1.7; color: ${H.INK}; margin: 0;
    }
  }

  /* ── Reduced motion: static split layout ── */
  @media (prefers-reduced-motion: reduce) {
    .vtr-scroll { height: auto; }
    .vtr-inner  { position: relative; min-height: 100vh; }
    .vtr-video, .vtr-dim, .vtr-overlay { display: none; }
  }
`;

/* ===================================================================
   StaticImageSection
   Static split layout: text LEFT, image RIGHT.
   Same proportions and typography as wim-next (WhyItMattersTransition).
================================================================== */
function StaticImageSection() {
  return (
    <>
      <style>{HSI_STYLES}</style>
      <section className="hsi-section" aria-label="Why It Matters — Himalayan Produce">
        <div className="hsi-inner">
          <div className="hsi-text">
            <span className="hsi-text__eyebrow">Why It Matters</span>
            <h2 className="hsi-text__title">Nothing Added. Nothing Removed.</h2>
            <p className="hsi-text__body">
              No polishing. No sorting by machine. No colour. No filler. No chemicals. Just what a handful of mountain farmers picked, dried, and sent down reaching you exactly as the land gave it.
            </p>
          </div>
          <img
            src={homeImg1001}
            alt="Himalayan farmers at high altitude"
            className="hsi-image"
            loading="lazy"
          />
        </div>
        <div className="wim-next__features">
          <div className="wtn-feature">
            <div className="wtn-feature__icon"><IcoFarmAltitude size={70}/></div>
            <h3 className="wtn-feature__title">Himalayan Sourced</h3>
            <p className="wtn-feature__text">Directly from pristine mountain farms</p>
          </div>
          <div className="wtn-feature">
            <div className="wtn-feature__icon"><IcoPlantProtein size={70}/></div>
            <h3 className="wtn-feature__title">100% Organic</h3>
            <p className="wtn-feature__text">No chemicals or pesticides</p>
          </div>
          <div className="wtn-feature">
            <div className="wtn-feature__icon"><IcoHowToCook size={70}/></div>
            <h3 className="wtn-feature__title">Traceability</h3>
            <p className="wtn-feature__text">Track your food from farm to table</p>
          </div>
          <div className="wtn-feature">
            <div className="wtn-feature__icon"><IcoWomenFarmers size={70}/></div>
            <h3 className="wtn-feature__title">Support Farmers</h3>
            <p className="wtn-feature__text">Fair prices for mountain communities</p>
          </div>
        </div>
      </section>
    </>
  );
}

const HSI_STYLES = `
  .hsi-section {
    position: relative;
    background: ${H.CREAM_SOFT};
    padding: 0 6vw;
    box-sizing: border-box;
  }
  .hsi-inner {
    display: flex; align-items: center; gap: 60px;
    max-width: 1320px; margin: 0 auto; width: 100%;
    padding: 80px 0;
  }

  /* Text — left */
  .hsi-text { flex: 1 1 55%; max-width: 620px; }
  .hsi-text__eyebrow {
    display: inline-block;
    font-size: 13px; letter-spacing: 0.28em; text-transform: uppercase;
    font-weight: 700; color: ${H.MEADOW_GOLD}; margin-bottom: 18px;
  }
  .hsi-text__title {
    font-family: Georgia, "Playfair Display", serif;
    font-size: clamp(30px, 3.4vw, 52px);
    line-height: 1.08; font-weight: 700;
    color: ${H.DEEP_FOREST}; margin: 0 0 22px;
  }
  .hsi-text__body {
    font-size: clamp(15px, 1.05vw, 17px);
    line-height: 1.7; color: ${H.INK}; margin: 0;
  }

  /* Image — right */
  .hsi-image {
    flex: 0 0 clamp(280px, 38vw, 560px);
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: 16px;
    display: block;
    width: clamp(280px, 38vw, 560px);
  }

  /* Tablet ≤900px: stack vertically */
  @media (max-width: 900px) {
    .hsi-inner {
      flex-direction: column; align-items: stretch;
      justify-content: center; gap: 24px;
    }
    .hsi-text { max-width: 100%; flex: 0 0 auto; }
    .hsi-image {
      flex: 0 0 auto; align-self: center;
      width: 82vw; height: auto;
    }
  }

  /* Mobile ≤560px */
  @media (max-width: 560px) {
    .hsi-section { padding: 0 5vw; }
    .hsi-inner { gap: 20px; }
    .hsi-image { width: 88vw; }
    .hsi-text__body { font-size: clamp(14px, 3.8vw, 16px); }
  }
`;

export default Home;
